import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight, ArrowLeft, Video } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { EVENT_TYPES, SERVICES } from "@/data/content";

const SERVICE_NAMES = SERVICES.map((s) => s.title);

const emptyForm = {
  name: "", email: "", phone: "", event_type: "", event_date: "",
  location: "", guest_count: "", service: "", budget: "", description: "",
};

function Field({ label, children }) {
  return (
    <div>
      <label className="label-lux block mb-2">{label}</label>
      {children}
    </div>
  );
}

export default function Quote() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(emptyForm);
  const [enquiryId, setEnquiryId] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [booking, setBooking] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (step === 2) {
      api.get("/slots").then((r) => setSlots(r.data)).catch(() => setSlots([]));
    }
  }, [step]);

  const submitEnquiry = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/enquiries", form);
      setEnquiryId(data.id);
      setStep(2);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not submit. Please try again.");
    } finally { setBusy(false); }
  };

  const confirmBooking = async () => {
    if (!selectedSlot) return;
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/bookings", {
        slot_id: selectedSlot.id, name: form.name, email: form.email, enquiry_id: enquiryId,
      });
      setBooking(data);
      setStep(3);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not book that time. Please choose another.");
      api.get("/slots").then((r) => setSlots(r.data)).catch(() => {});
      setSelectedSlot(null);
    } finally { setBusy(false); }
  };

  const groupedSlots = slots.reduce((acc, s) => {
    const d = new Date(s.starts_at);
    const key = d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", timeZone: "Europe/London" });
    (acc[key] = acc[key] || []).push(s);
    return acc;
  }, {});

  return (
    <div data-testid="quote-page" className="pt-28 md:pt-32 min-h-screen">
      <section className="container-lux py-12 md:py-16">
        <div className="max-w-3xl">
          <p className="overline mb-5">Begin Your Journey</p>
          <h1 className="font-serif-lux text-4xl md:text-6xl font-light leading-[1.05] mb-6">
            Tell us about your celebration.
          </h1>
          <p className="text-[#4A4A4A] text-lg leading-relaxed">
            Share your vision and we'll be in touch. The next step is a complimentary Zoom
            consultation to bring it all to life.
          </p>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-3 md:gap-5 mt-12 mb-4">
          {["Your Enquiry", "Book Consultation", "Confirmed"].map((label, i) => {
            const n = i + 1;
            const done = step > n;
            const current = step === n;
            return (
              <div key={label} className="flex items-center gap-3">
                <span className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  done ? "bg-[#D4AF37] text-black" : current ? "bg-[#0A0A0A] text-white" : "bg-[#E5E5DF] text-[#8a8a80]"
                }`}>
                  {done ? <Check size={16} /> : n}
                </span>
                <span className={`hidden sm:block text-xs uppercase tracking-[0.14em] ${current ? "text-[#0A0A0A]" : "text-[#8a8a80]"}`}>{label}</span>
                {i < 2 && <span className="w-6 md:w-12 h-px bg-[#E5E5DF]" />}
              </div>
            );
          })}
        </div>
      </section>

      <section className="container-lux pb-28">
        {error && (
          <div data-testid="quote-error" className="mb-8 border border-red-300 bg-red-50 text-red-700 px-5 py-3 text-sm">
            {error}
          </div>
        )}

        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.form
              key="s1" onSubmit={submitEnquiry} data-testid="enquiry-form"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              className="grid md:grid-cols-2 gap-x-12 gap-y-8 max-w-4xl"
            >
              <Field label="Full Name *"><input required data-testid="q-name" className="input-lux" value={form.name} onChange={set("name")} placeholder="Your name" /></Field>
              <Field label="Email *"><input required type="email" data-testid="q-email" className="input-lux" value={form.email} onChange={set("email")} placeholder="you@email.com" /></Field>
              <Field label="Phone *"><input required data-testid="q-phone" className="input-lux" value={form.phone} onChange={set("phone")} placeholder="Phone number" /></Field>
              <Field label="Event Type *">
                <select required data-testid="q-event-type" className="input-lux" value={form.event_type} onChange={set("event_type")}>
                  <option value="">Select…</option>
                  {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Preferred Date"><input type="date" data-testid="q-date" className="input-lux" value={form.event_date} onChange={set("event_date")} /></Field>
              <Field label="Location"><input data-testid="q-location" className="input-lux" value={form.location} onChange={set("location")} placeholder="City / venue" /></Field>
              <Field label="Guest Numbers"><input data-testid="q-guests" className="input-lux" value={form.guest_count} onChange={set("guest_count")} placeholder="Approx. guests" /></Field>
              <Field label="Service Required">
                <select data-testid="q-service" className="input-lux" value={form.service} onChange={set("service")}>
                  <option value="">Select…</option>
                  {SERVICE_NAMES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Budget Range (optional)"><input data-testid="q-budget" className="input-lux" value={form.budget} onChange={set("budget")} placeholder="If you'd like to share" /></Field>
              <div className="hidden md:block" />
              <div className="md:col-span-2">
                <Field label="Your Vision / Event Description">
                  <textarea data-testid="q-description" rows={4} className="input-lux resize-none" value={form.description} onChange={set("description")} placeholder="Tell us about the celebration you're dreaming of…" />
                </Field>
              </div>
              <div className="md:col-span-2 pt-2">
                <button type="submit" disabled={busy} data-testid="enquiry-submit-btn" className="btn-gold disabled:opacity-60">
                  {busy ? "Submitting…" : <>Continue to Booking <ArrowRight size={16} className="ml-2" /></>}
                </button>
              </div>
            </motion.form>
          )}

          {step === 2 && (
            <motion.div
              key="s2" data-testid="booking-step"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              className="max-w-4xl"
            >
              <h2 className="font-serif-lux text-3xl font-light mb-3">Book your consultation</h2>
              <p className="text-[#4A4A4A] mb-10">Choose a time that suits you. We'll meet over Zoom to discuss your event. All times are shown in UK time.</p>

              {Object.keys(groupedSlots).length === 0 ? (
                <p className="text-[#8a8a80] py-12">No consultation times available right now — we'll reach out to arrange one.</p>
              ) : (
                <div className="space-y-8">
                  {Object.entries(groupedSlots).map(([day, daySlots]) => (
                    <div key={day}>
                      <h3 className="text-sm uppercase tracking-[0.14em] text-[#8a8a80] mb-4">{day}</h3>
                      <div className="flex flex-wrap gap-3">
                        {daySlots.map((s) => {
                          const time = new Date(s.starts_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" });
                          const sel = selectedSlot?.id === s.id;
                          return (
                            <button
                              key={s.id}
                              data-testid={`slot-${s.id}`}
                              onClick={() => setSelectedSlot(s)}
                              className={`px-6 py-3 border text-sm transition-colors ${
                                sel ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#1A1A1A]/20 hover:border-[#D4AF37]"
                              }`}
                            >
                              {time}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 mt-12">
                <button onClick={() => setStep(1)} className="btn-outline-dark" data-testid="booking-back-btn">
                  <ArrowLeft size={16} className="mr-2" /> Back
                </button>
                <button onClick={confirmBooking} disabled={!selectedSlot || busy} data-testid="booking-confirm-btn" className="btn-gold disabled:opacity-50">
                  {busy ? "Booking…" : "Confirm Consultation"}
                </button>
              </div>
            </motion.div>
          )}

          {step === 3 && booking && (
            <motion.div
              key="s3" data-testid="confirmation-step"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="max-w-2xl"
            >
              <div className="w-16 h-16 rounded-full bg-[#D4AF37] flex items-center justify-center mb-8">
                <Check className="text-black" size={30} />
              </div>
              <h2 className="font-serif-lux text-4xl font-light mb-4">You're all set, {form.name.split(" ")[0]}.</h2>
              <p className="text-[#4A4A4A] text-lg mb-2">Your consultation is confirmed for:</p>
              <p className="font-serif-lux text-2xl text-[#0A0A0A] mb-8">{booking.when}</p>

              {booking.join_url ? (
                <a href={booking.join_url} target="_blank" rel="noreferrer" data-testid="zoom-join-link" className="btn-gold inline-flex">
                  <Video size={18} className="mr-2" /> Join Zoom Consultation
                </a>
              ) : (
                <div className="border border-[#E5E5DF] bg-[#F1EFE8] px-6 py-5 text-[#4A4A4A]">
                  Your Zoom meeting link will be emailed to <strong>{form.email}</strong> shortly before the consultation.
                </div>
              )}
              <p className="text-sm text-[#8a8a80] mt-8">A confirmation has been sent to {form.email}.</p>
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    </div>
  );
}
